import { createSvgIcon } from "@/client/ui/design-system/icons";
import { colors } from "@/client/ui/design-system/styles/colors.css";
import { createEl, resizeCanvas2D } from "@/client/utils/createEl";
import { getSpliceSampleDisplayName } from "@/client/utils/createSpliceSampleInputFile";
import type { RemoveListener } from "@/client/utils/signal";
import type { SpliceSearchResult } from "@/shared/splice-search.api";
import { sampleTargetsSignal } from "@/client/utils/sample-targets";
import { hasSeededMode } from "./modes/sounds-browser-modes";
import { kPresetAssetCategory } from "./preset-results";
import { formatSampleBpm, formatSampleDuration, formatSampleKey } from "./sample-format";
import type { SoundsBrowserContext } from "./sounds-browser.api";
import * as styles from "./sounds-browser.css";

export interface SampleRowComponent {
    readonly el: HTMLElement;
    readonly result: SpliceSearchResult;
    /** Redraws the waveform at the row's current width. */
    readonly redraw: () => void;
    /** Fetches and decodes the preview so the waveform can be drawn. Once. */
    readonly warm: () => void;
    readonly dispose: () => void;
}

export interface SampleRowOptions {
    readonly onTagClick: (tag: string) => void;
    /** Search from this sample. Shown only while a seeded mode is on offer. */
    readonly onFindSimilar: (result: SpliceSearchResult) => void;
    /**
     * Put this sample where the page takes one, without a drag. Shown only
     * while the page has a sample target (see `sample-targets.ts`); the
     * anchor is the button, for a chooser when there is more than one.
     */
    readonly onUse: (result: SpliceSearchResult, anchor: HTMLElement) => void;
}

const kMaxTags = 3;
const kWaveformHeight_px = 24;

/**
 * One catalog sample, laid out the way the plugin's asset tile is: pack art
 * beside two lines, the name and tags above, play, waveform and the
 * time / key / BPM cluster below. The whole row is the drag handle.
 */
export const createSampleRow = (
    context: SoundsBrowserContext,
    result: SpliceSearchResult,
    options: SampleRowOptions,
): SampleRowComponent => {
    const { actions, previews, player, signals } = context;
    const { metadata } = result;
    const removeListeners: RemoveListener[] = [];
    let rafId: number | undefined;

    // ── Art ──────────────────────────────────────────────────────────────────
    const fallbackIcon = createSvgIcon(
        metadata.assetCategory === kPresetAssetCategory
            ? "preset"
            : metadata.assetCategory === "oneshot"
              ? "oneshot"
              : "loop",
        styles.artIcon,
    );
    const artEl = createEl("div", styles.art, [fallbackIcon], (el) => {
        if (metadata.packArtworkUrl === "") {
            return;
        }
        const img = createEl("img", "", [], (imgEl) => {
            imgEl.alt = "";
            imgEl.loading = "lazy";
            imgEl.decoding = "async";
            imgEl.draggable = false;
            imgEl.addEventListener("error", () => {
                imgEl.remove();
            });
            imgEl.src = metadata.packArtworkUrl;
        });
        el.appendChild(img);
    });

    // ── Line 1: name and tags ────────────────────────────────────────────────
    const displayName = getSpliceSampleDisplayName(metadata.name);
    const nameEl = createEl("p", styles.sampleName, displayName, (el) => {
        el.title = metadata.packName !== "" ? `${displayName} · ${metadata.packName}` : displayName;
    });
    const tagsEl = createEl(
        "div",
        styles.rowTags,
        metadata.tags.slice(0, kMaxTags).map((tag) =>
            createEl("button", styles.rowTag, tag, (el) => {
                el.type = "button";
                el.title = `Search "${tag}"`;
                el.addEventListener("click", (event) => {
                    event.stopPropagation();
                    options.onTagClick(tag);
                });
            }),
        ),
    );

    // Search with Sound searches the catalog, so the sparkle hides with the
    // zone while the library is the scope.
    const isFindSimilarShown = (): boolean =>
        hasSeededMode(context.state.visibleModes) && !context.state.filters.isLibraryOnly;
    const findSimilarButton = createEl(
        "button",
        styles.rowAction,
        [createSvgIcon("searchSparkles", styles.rowActionIcon)],
        (el) => {
            el.type = "button";
            el.title = "Find similar sounds";
            el.setAttribute("aria-label", `Find sounds similar to ${displayName}`);
            el.draggable = false;
            el.style.display = isFindSimilarShown() ? "" : "none";
            el.addEventListener("click", (event) => {
                event.stopPropagation();
                options.onFindSimilar(result);
            });
        },
    );
    removeListeners.push(
        signals.visibleModes.listen(() => {
            findSimilarButton.style.display = isFindSimilarShown() ? "" : "none";
        }),
        signals.filters.listen(() => {
            findSimilarButton.style.display = isFindSimilarShown() ? "" : "none";
        }),
    );

    // The tap-to-use action: present only while the page has somewhere to
    // put a sample, which the home page never does.
    const useButton = createEl(
        "button",
        styles.rowAction,
        [createSvgIcon("addToStack", styles.rowActionIcon)],
        (el) => {
            el.type = "button";
            el.classList.add(styles.rowUseInline);
            el.title = "Use this sample";
            el.setAttribute("aria-label", `Use ${displayName}`);
            el.draggable = false;
            el.style.display = sampleTargetsSignal.value.length > 0 ? "" : "none";
            el.addEventListener("click", (event) => {
                event.stopPropagation();
                options.onUse(result, el);
            });
        },
    );
    // At phone width the same action is a larger button at the row's right
    // edge, centred on both lines; the inline one hides there (see the css).
    const useButtonLarge = createEl(
        "button",
        styles.rowUseLarge,
        [createSvgIcon("addToStack", styles.rowUseLargeIcon)],
        (el) => {
            el.type = "button";
            el.title = "Use this sample";
            el.setAttribute("aria-label", `Use ${displayName}`);
            el.draggable = false;
            el.style.display = sampleTargetsSignal.value.length > 0 ? "" : "none";
            el.addEventListener("click", (event) => {
                event.stopPropagation();
                options.onUse(result, el);
            });
        },
    );
    removeListeners.push(
        sampleTargetsSignal.listen((targets) => {
            useButton.style.display = targets.length > 0 ? "" : "none";
            useButtonLarge.style.display = targets.length > 0 ? "" : "none";
        }),
    );

    // ── Line 2: play, waveform, meta ─────────────────────────────────────────
    const playIcon = createSvgIcon("playSolid", styles.playIcon);
    const stopIcon = createSvgIcon("stopSolid", styles.playIcon);
    const spinnerEl = createEl("span", styles.playSpinner);
    const playButton = createEl("button", styles.playButton, [playIcon], (el) => {
        el.type = "button";
        el.setAttribute("aria-label", `Play ${displayName}`);
        el.draggable = false;
        el.addEventListener("click", (event) => {
            event.stopPropagation();
            actions.togglePreview(result);
        });
    });

    const canvas = createEl("canvas", styles.waveformCanvas);
    const ctx = canvas.getContext("2d");
    const placeholderEl = createEl("span", styles.waveformPlaceholder);
    const waveformEl = createEl("div", styles.waveform, [placeholderEl, canvas], (el) => {
        el.addEventListener("click", () => actions.togglePreview(result));
    });

    // A preset has no key, tempo or length to show. Its row keeps the slot
    // for what a surface will be able to do with a preset, empty for now.
    const isPreset = metadata.assetCategory === kPresetAssetCategory;
    const metaEl = isPreset
        ? createEl("div", styles.rowActions, [])
        : createEl("div", styles.meta, [
              createEl(
                  "span",
                  `${styles.metaCell} ${styles.metaKey}`,
                  formatSampleKey(metadata.key, metadata.chordType),
              ),
              createEl(
                  "span",
                  `${styles.metaCell} ${styles.metaBpm}`,
                  formatSampleBpm(metadata.bpm),
              ),
              createEl(
                  "span",
                  `${styles.metaCell} ${styles.metaTime}`,
                  formatSampleDuration(metadata.duration),
              ),
          ]);

    // ── Waveform drawing ─────────────────────────────────────────────────────
    let drawnWidth = 0;
    let isPlayingThisRow = false;

    const draw = (): void => {
        if (ctx === null) {
            return;
        }
        const preview = previews.peek(metadata.uuid);
        if (preview === undefined) {
            return;
        }
        const width = Math.floor(waveformEl.clientWidth);
        if (width <= 0) {
            return;
        }
        if (width !== drawnWidth) {
            drawnWidth = width;
            // `resizeCanvas2D` scales by the device pixel ratio each time it
            // runs, and resizing a canvas resets its transform, so this stays
            // one scale rather than compounding.
            resizeCanvas2D(canvas, ctx, width, kWaveformHeight_px, "100%", "100%");
        }
        placeholderEl.style.display = "none";
        const path = preview.waveform.getPath(width, kWaveformHeight_px, true);
        ctx.clearRect(0, 0, width, kWaveformHeight_px);
        ctx.fillStyle = colors.waveformFill;
        ctx.fill(path);

        const progress = isPlayingThisRow ? player.getProgress_0to1() : (resumeFraction ?? 0);
        {
            if (progress > 0) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, 0, width * progress, kWaveformHeight_px);
                ctx.clip();
                ctx.fillStyle = colors.waveformProgress;
                ctx.fill(path);
                ctx.restore();
            }
        }
    };

    const stopTicker = (): void => {
        if (rafId !== undefined) {
            cancelAnimationFrame(rafId);
            rafId = undefined;
        }
    };

    const tick = (): void => {
        draw();
        rafId = requestAnimationFrame(tick);
    };

    const setPlaying = (isPlaying: boolean, isBuffering: boolean): void => {
        isPlayingThisRow = isPlaying;
        el.classList.toggle(styles.rowPlaying, isPlaying);
        playButton.replaceChildren(isPlaying ? (isBuffering ? spinnerEl : stopIcon) : playIcon);
        playButton.setAttribute("aria-label", `${isPlaying ? "Stop" : "Play"} ${displayName}`);
        stopTicker();
        if (isPlaying) {
            rafId = requestAnimationFrame(tick);
        } else {
            draw();
        }
    };

    // The row that was last played or searched from stays lit, and the one
    // the previous page was playing offers to pick up where it left off.
    removeListeners.push(
        signals.selected.listen((uuid) => {
            el.classList.toggle(styles.rowSelected, uuid === metadata.uuid);
        }),
    );
    let resumeFraction: number | undefined;
    const applyResume = (resume: { uuid: string; position_s: number } | undefined): void => {
        const isThisRow = resume?.uuid === metadata.uuid;
        resumeFraction =
            isThisRow && metadata.duration > 0
                ? Math.min(resume.position_s / (metadata.duration / 1000), 1)
                : undefined;
        if (!isPlayingThisRow) {
            playButton.setAttribute(
                "aria-label",
                `${isThisRow ? "Resume" : "Play"} ${displayName}`,
            );
            playButton.title = isThisRow ? "Resume" : "";
            draw();
        }
    };
    removeListeners.push(signals.resume.listen(applyResume));

    removeListeners.push(
        signals.playback.listen((playback) => {
            const isThisRow = playback.isPlaying && playback.uuid === metadata.uuid;
            setPlaying(isThisRow, playback.isPlaying && isThisRow && playback.isBuffering);
        }),
    );

    // Called by the list when the row scrolls into view, so a long list does
    // not fetch every preview at once. The preview may already be decoded (a
    // re-render of the same results) or arrive later.
    let didWarm = false;
    const warm = (): void => {
        if (didWarm) {
            return;
        }
        didWarm = true;
        void previews.ensure(result).then((preview) => {
            if (preview instanceof Error) {
                actions.reportPreviewFailure(result);
                return;
            }
            if (el.isConnected) {
                draw();
            }
        });
    };

    // ── Row ──────────────────────────────────────────────────────────────────
    const el = createEl(
        "div",
        styles.row,
        [
            artEl,
            createEl("div", styles.tile, [
                createEl("div", styles.tileLine, [nameEl, tagsEl, findSimilarButton, useButton]),
                createEl("div", styles.tileLine, [playButton, waveformEl, metaEl]),
            ]),
            useButtonLarge,
        ],
        (rowEl) => {
            rowEl.draggable = true;
            rowEl.dataset.uuid = metadata.uuid;
            rowEl.addEventListener("dragstart", (event) => {
                if (event.dataTransfer === null) {
                    return;
                }
                actions.beginDrag(result, event.dataTransfer);
                rowEl.classList.add(styles.rowDragging);
            });
            rowEl.addEventListener("dragend", () => {
                rowEl.classList.remove(styles.rowDragging);
            });
            rowEl.addEventListener("dblclick", () => actions.togglePreview(result));
        },
    );

    const initialPlayback = player.getState();
    if (initialPlayback.isPlaying && initialPlayback.uuid === metadata.uuid) {
        setPlaying(true, initialPlayback.isBuffering);
    }
    el.classList.toggle(styles.rowSelected, context.state.selectedUuid === metadata.uuid);
    applyResume(context.state.resume);

    if (previews.peek(metadata.uuid) !== undefined) {
        didWarm = true;
        requestAnimationFrame(draw);
    }

    return {
        el,
        result,
        redraw: draw,
        warm,
        dispose: () => {
            stopTicker();
            for (const remove of removeListeners) {
                remove();
            }
        },
    };
};
